/**
 * Copyright 2024-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useState } from "react";
import { When } from "react-if";
import { Label, Select, Text, theme, Modal, Message } from "@nordcloud/gnui";
import { FormGroup } from "~/components";
import {
  isBaselineDependentEntity,
  isBaselineSourceEntity,
  isBaselinePatchingEnabled as isBaselinePatching,
  isBelowBaselineSourceEntity,
  isPatchOrScanEntity,
  updateBaselineDependentEntities,
} from "~/utils";
import { baselineStrings } from "~/views/plans/constants";
import { PlanField } from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import { usePlanWizard } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanProvider";
import { useStepState } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/StepProvider";
import { FormField } from "~/views/plans/PlanCreate/components/PlanCreateWizard/types";
import { updateEntities } from "../utils";

type Option = {
  label: string;
  value: boolean;
  description?: string;
};

const baselineOptions: Option[] = [
  {
    label: "Disabled",
    value: false,
  },
  {
    label: "Enabled",
    value: true,
  },
];

export function SelectBaselinePatching() {
  const { stepState, updateStepState } = useStepState();
  const { planData, setPlanData } = usePlanWizard();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const [baselineSourceDropdownValue, setBaselineSourceDropdownValue] =
    useState(false);

  const patchScanEntitiesInPlan = useMemo(() => {
    const entities = planData?.[PlanField.PLAN_SETTINGS]?.planEntities || [];
    return entities.reduce<Array<NonNullable<typeof stepState.selectedEntity>>>(
      (acc, item) => {
        if (item && isPatchOrScanEntity(item)) {
          return [...acc, item];
        }

        if (item?.batchActions?.length) {
          const batchPatches = item.batchActions.filter(
            (batchItem): batchItem is NonNullable<typeof batchItem> =>
              !!batchItem && isPatchOrScanEntity(batchItem)
          );
          return [...acc, ...batchPatches];
        }

        return acc;
      },
      []
    );
  }, [planData, stepState]);

  const patchScanEntitiesInBatch = useMemo(
    () =>
      patchScanEntitiesInPlan.filter((item) => {
        if (item.batchId && stepState.selectedEntity?.batchId) {
          return item.batchId === stepState.selectedEntity.batchId;
        }
      }),
    [patchScanEntitiesInPlan, stepState.selectedEntity]
  );

  const isEntityBaselineSource = useMemo(
    () => isBaselineSourceEntity(stepState.selectedEntity),
    [stepState.selectedEntity]
  );

  const isBaselinePatchingEnabled = useMemo(
    () => isBaselinePatching(patchScanEntitiesInPlan ?? []),
    [patchScanEntitiesInPlan]
  );

  useEffect(() => {
    if (
      stepState.selectedEntity &&
      baselineSourceDropdownValue !== isEntityBaselineSource
    ) {
      setBaselineSourceDropdownValue(isEntityBaselineSource);
    }
  }, [baselineSourceDropdownValue, stepState.selectedEntity]);

  if (!isPatchOrScanEntity(stepState.selectedEntity)) {
    return null;
  }

  const handleChange = (val: boolean) => {
    setBaselineSourceDropdownValue(val);

    const existingParameters = stepState.selectedEntity?.inputParameters ?? [];

    const updatedInputParameters = [
      ...existingParameters.filter((param) => param.key !== "BaselineSource"),
      { key: "BaselineSource", value: val.toString() },
    ];

    const updateObject = {
      [FormField.INPUT_PARAMETERS]: updatedInputParameters,
    };

    const updatedEntities = updateEntities(stepState, updateObject) ?? [];

    const updatedWithDependentEntities = updateBaselineDependentEntities(
      updatedEntities,
      stepState.selectedEntity,
      val
    );

    updateStepState({
      selectedEntity: { ...stepState.selectedEntity, ...updateObject },
      entities: updatedWithDependentEntities,
    });

    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.PLAN_SETTINGS]: {
        planEntities: updatedWithDependentEntities,
      },
    }));
  };

  const handleModalConfirm = () => {
    if (pendingValue !== null) {
      handleChange(pendingValue);
    }
    setShowConfirmModal(false);
    setPendingValue(null);
  };

  const handleSelectChange = (val: boolean) => {
    setShowConfirmModal(true);
    setPendingValue(val);
  };

  const formatOptionLabel = ({ label, description }: Option) => (
    <div>
      <Text tag="div">{label}</Text>
      <When condition={description}>
        <Text tag="div" size="sm" color={theme.color.text.text02}>
          {description}
        </Text>
      </When>
    </div>
  );

  return (
    <>
      <FormGroup>
        <Label name="Baseline Patching Source" css={{ marginBottom: 0 }} />
        <Text
          mb={theme.spacing.spacing02}
          color={theme.color.text.text02}
          size="sm"
          tag="div"
        >
          {baselineStrings.baselineInfo}
        </Text>
        <When
          condition={
            isBaselineDependentEntity(
              stepState?.selectedEntity,
              patchScanEntitiesInPlan
            ) &&
            isBelowBaselineSourceEntity(
              stepState?.selectedEntity,
              patchScanEntitiesInPlan
            )
          }
        >
          <Message
            status="notification"
            image="info"
            css={{ marginBottom: "16px" }}
          >
            {baselineStrings.baselineDisabledDropdownInfo}
          </Message>
        </When>

        <When
          condition={
            patchScanEntitiesInBatch.length > 1 && !isBaselinePatchingEnabled
          }
        >
          <Message
            status="notification"
            image="info"
            css={{ marginBottom: "16px" }}
          >
            {baselineStrings.baselineDisabledDropdownInfoMultipleActions}
          </Message>
        </When>
        <Select
          isDisabled={
            (!isEntityBaselineSource && isBaselinePatchingEnabled) ||
            patchScanEntitiesInBatch.length > 1
          }
          isSearchable={false}
          name="Select Baseline Patching"
          value={{
            label: baselineSourceDropdownValue
              ? baselineOptions[1].label
              : baselineOptions[0].label,
            value: baselineSourceDropdownValue,
          }}
          onChange={(g: Option | null) => handleSelectChange(g?.value ?? false)}
          options={baselineOptions}
          formatOptionLabel={formatOptionLabel}
        />
      </FormGroup>
      <When condition={showConfirmModal}>
        <Modal
          contentLabel="Baseline Action"
          isOpen
          onClose={() => {
            setShowConfirmModal(false);
            setPendingValue(null);
          }}
          actions={[
            {
              label: "Cancel",
              onAction: () => {
                setShowConfirmModal(false);
                setPendingValue(null);
              },
              severity: "low",
              order: 1,
            },
            {
              label: "OK",
              onAction: handleModalConfirm,
              severity: "high",
              order: 2,
            },
          ]}
        >
          {pendingValue
            ? baselineStrings.confirmationModalEnable
            : baselineStrings.confirmationModalDisable}
        </Modal>
      </When>
    </>
  );
}
